import React, { Component } from 'react'
import { SCREEN_SIZE } from '../../lib/Media'
import VirtualCarousel from '../VirtualCarousel'
import styled from 'styled-components'
import defaultTheme from '@ntuctech/devex-tangram/Theme/defaultTheme'
import { CheckoutAddressConsumer } from '../CheckoutAddressProvider/CheckoutAddressProvider'
import CategoryCard from '../CategoryCard'
import Text from '../Text'
import Link from 'next/link'
import {
  trackingTypes,
  createNewObserver,
  impressionTypes,
  triggerImpressionsTracking,
} from '../../lib/impressionsTracker'
import { trackPromoImpressions } from '../../lib/trackClicksAndImpressions'
import {
  GTM_ENV_VAR as GEV,
  getNCheckFn as getNCheck,
  SPLIT_EVENT,
} from '../../lib/eventTrackerFunction'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { AMP_EVENT_NAME } from '../../lib/amplitude'

const CategoryContainer = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0 1.8125rem 0;
`
const CategoryCardContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`
const HeadingDiv = styled.div`
  margin-bottom: 1rem;
  display: flex;
  position: relative;
`

const SubHeader = styled(Text)`
  font-size: 1rem;
  ${SCREEN_SIZE.Below.Tablet} {
    font-size: 0.875rem;
  }
`

const StyledLink = styled(Text)`
  text-decoration: none;
  margin-left: auto;
  position: absolute;
  right: 0;
  bottom: 0;
`

const PageHeading = styled.h2`
  font-weight: 700;
  color: #333333;
  font-size: 1.25rem;
  line-height: 1.4rem;
  ${SCREEN_SIZE.Below.Tablet} {
    font-size: 1rem;
  }
`

class CategoryCollection extends Component {
  constructor(props) {
    super(props)

    this.state = {
      trackedBanners: false,
    }

    this.createObserver = this.createObserver.bind(this)
    this.getCategoryCard = this.getCategoryCard.bind(this)
    this.getCategoryComponent = this.getCategoryComponent.bind(this)
    this.trackBannerImpressions = this.trackBannerImpressions.bind(this)
    this.handleTrackSeeAllClick = this.handleTrackSeeAllClick.bind(this)
  }

  trackBannerImpressions(
    params = {
      action: '',
      prevStoreId: '',
      prevDBPStoreId: '',
    }
  ) {
    const storeIdToTrack =
      params.prevStoreId ||
      getNCheck(this.props.checkoutAddress, 'clientId', '').toString()
    const dbpStoreIdToTrack =
      params.prevDBPStoreId ||
      getNCheck(this.props.checkoutAddress, 'storeId', '').toString()
    trackPromoImpressions({
      track: this.props.track,
      trackSplit: this.props.trackSplit,
      storeIdToTrack,
      dbpStoreIdToTrack,
      banners: [],
      currentType: impressionTypes.OTHER_BANNER_IMPRESSION,
      params,
    })

    if (params.action === 'trackOnUpdate' && !this.state.trackedBanners) {
      this.setState({ trackedBanners: true })
    }
  }

  /**
   * Creates observer for all given elements
   * sets minimum category card visibility to 1% for tracking impression
   */
  createObserver({ productRef, bannerTrackObj, callAction }) {
    if (
      callAction === 'onLoad' ||
      (callAction === 'afterUpdate' && this.state.trackedBanners)
    ) {
      createNewObserver({
        productRef: productRef,
        trackType: trackingTypes.TRACK_ONCE, // signifies tracked once per page load
        productTrackingData: bannerTrackObj,
        splitTrackingData: {
          ...bannerTrackObj,
          event_type: SPLIT_EVENT,
          [GEV.EVENT]: 'splitPromoImpression',
        },
        options: {
          root: null,
          rootMargin: '0px',
          threshold: 0.01, // signifies that min 1% of the product card is visible
        },
        impressionType: impressionTypes.OTHER_BANNER_IMPRESSION,
        splitImpressionType: impressionTypes.SPLIT_OTHER_BANNER_IMPRESSION,
      })

      if (this.state.trackedBanners) {
        this.setState({ trackedBanners: false })
      }
    }
  }

  componentDidMount() {
    triggerImpressionsTracking.subscribe(
      impressionTypes.OTHER_BANNER_IMPRESSION,
      data => {
        if (data === impressionTypes.OTHER_BANNER_IMPRESSION) {
          this.trackBannerImpressions()
        }
      }
    )
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.checkoutAddress.storeId !== this.props.checkoutAddress.storeId
    ) {
      this.trackBannerImpressions({
        action: 'trackOnUpdate',
        prevStoreId: getNCheck(
          prevProps.checkoutAddress,
          'clientId',
          ''
        ).toString(),
        prevDBPStoreId: getNCheck(
          prevProps.checkoutAddress,
          'storeId',
          ''
        ).toString(),
      })
    }
  }

  handleTrackSeeAllClick() {
    const { track, data, config } = this.props
    const title = data?.title
    const eventData = {
      [GEV.EVENT_CAT]: 'Ecommerce-Engagement',
      [GEV.EVENT_ACTION]: 'LinkClick',
      [GEV.EVENT_LABEL]: `Label=SeeAll-${title}`,
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.WIDGET_SEE_ALL_CLICKED,
      [GEV.AMPLITUDE_CTA_SUBLOCATION]: config?.ctaSublocation,
      [GEV.AMPLITUDE_CTA_LOCATION]: config?.ctaLocation,
    }
    track(eventData)
  }

  componentWillUnmount() {
    triggerImpressionsTracking.unsubscribe(
      impressionTypes.OTHER_BANNER_IMPRESSION
    )
  }

  getCategoryCard(collections, showProductCount, title) {
    const { config } = this.props
    return (
      <VirtualCarousel
        items={collections}
        staticHeight={{
          [SCREEN_SIZE.From.MobileL]: config.subcategories ? 154 : 211,
          [SCREEN_SIZE.Below.MobileL]: config.subcategories ? 162 : 158,
        }}
        staticWidth={{
          [SCREEN_SIZE.From.MobileL]: config.subcategories ? 112 : 232,
          [SCREEN_SIZE.Below.MobileL]: config.subcategories ? 112 : 164,
        }}
      >
        {({ item, index }) => {
          return this.getCategoryComponent(item, index, showProductCount, title)
        }}
      </VirtualCarousel>
    )
  }

  getCategoryComponent(item, index, showProductCount, title) {
    const { config, checkoutAddress } = this.props
    return (
      <CategoryCard
        cardSize={config.subcategories}
        key={'category-' + index}
        collection={item}
        showProductCount={showProductCount}
        bannerPosition={index + 1}
        createObserver={this.createObserver}
        trackBannerImpressions={this.trackBannerImpressions}
        impressionType={impressionTypes.OTHER_BANNER_IMPRESSION}
        checkoutAddress={checkoutAddress}
        role="row"
        title={title}
        ctaLocation={config?.ctaLocation}
        ctaSublocation={config?.ctaSublocation}
      />
    )
  }

  render() {
    const { data, config } = this.props
    const title = data && data.title
    const collections = data && data.collection
    const { subtitle, layoutType, showProductCount, loadMoreType } =
      config || {}
    return !(collections && collections.length) ? null : (
      <CategoryContainer>
        {(title || subtitle) && (
          <HeadingDiv>
            <div>
              {title && (
                <PageHeading
                  size="large"
                  weight="bold"
                  color={defaultTheme.ColorBaseMineShaft700}
                  title={title}
                >
                  {title}
                </PageHeading>
              )}
              {subtitle && (
                <SubHeader
                  as="h3"
                  color={defaultTheme.ColorBaseMineShaft500}
                  size="large"
                >
                  {subtitle}
                </SubHeader>
              )}
            </div>
            {loadMoreType === 'SEEALL' && (
              <Link
                href={'/tagDetails?slug=marketplace'}
                as="/tag/marketplace"
                passHref
                prefetch={false}
                legacyBehavior>
                <StyledLink
                  as="a"
                  size="medium"
                  color={defaultTheme.ColorBaseBrandPrimary500}
                  weight="bold"
                  onClick={this.handleTrackSeeAllClick}
                >
                  See all
                </StyledLink>
              </Link>
            )}
          </HeadingDiv>
        )}
        {layoutType === 'SCROLLER' ? (
          this.getCategoryCard(collections, showProductCount, title)
        ) : (
          <CategoryCardContainer>
            {collections &&
              collections.map((collection, index) => {
                return this.getCategoryComponent(
                  collection,
                  index,
                  showProductCount,
                  title
                )
              })}
          </CategoryCardContainer>
        )}
      </CategoryContainer>
    );
  }
}

const CategoryCollectionWrap = props => (
  <CheckoutAddressConsumer>
    {({ checkoutAddress }) => (
      <EventTrackingConsumer>
        {({ track, trackSplit }) => (
          <React.Fragment>
            <div />
            {/* Needed for avoiding layout shifting in the home page */}
            <CategoryCollection
              {...props}
              track={track}
              trackSplit={trackSplit}
              checkoutAddress={checkoutAddress}
            />
          </React.Fragment>
        )}
      </EventTrackingConsumer>
    )}
  </CheckoutAddressConsumer>
)

CategoryCollectionWrap.defaultProps = {
  config: {},
}

export default CategoryCollectionWrap
